import { Component, HostBinding, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgClass } from '@angular/common';
import { Observable, Subscription, catchError, forkJoin, map, of, switchMap } from 'rxjs';
import moment from 'moment';
import { HttpErrorResponse } from '@angular/common/http';

import { BocClient } from '../../../shared/models/boc-client-transac';
import { FastLaneItems }
	from '../../models/fastlane-datacenter-config';
import { FastLaneService } from '../../services/fastlane.service';
import { pageTransitions } from '../../../shared/animations/page-transitions';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { OfflineComponent } from '../../../shared/components/offline/offline.component';
import { DASHES } from '../../../shared/utils/constant';
import { TranslationService } from '../../../shared/services/translation.service';
import { UserService } from '../../../shared/services/user.service';
import { User } from '../../../shared/models/user';
import { PrototypeStatusComponent } from '../../../shared/components/statustracker/status-tracker.component';
import { ProductCharacteristicsTranslationService } from
	'../../../shared/services/product-characteristics-translation.services';
import
{ CONFIRMED_STATUS_INDEX, DECISION_STATUS_INDEX, MATERIAL_AVAILABILTY_STATUS_INDEX, PrototypeStatus, currencyList }
	from '../../models/fastlane-list';
import { SoldToRelationshipGroup } from '../../../shared/models/service';
import { safe } from '../../../shared/utils/safe';
import { WEEKYEAR_FORMAT_PATTERN } from '../../../shared/models/filter-formats';
import { formatTextDimension } from '../../../shared/utils/number.format';

interface Responsedata {
	bocValueList: readonly [SoldToRelationshipGroup[], BocClient[]];
	prototypeCardData: FastLaneItems;
}
@Component({
	selector: 'copo-prototype-card',
	templateUrl: './prototype-card.component.html',
	styleUrl: './prototype-card.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		BackButtonComponent,
		TranslateModule,
		MatIconModule,
		MatButtonModule,
		MatExpansionModule,
		MatDividerModule,
		NgClass,
		PrototypeStatusComponent,
		OfflineComponent,
		LoadingSpinnerComponent,
		DatePipe,
	]
})

export class PrototypeCardComponent implements OnInit, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	readonly DASHES = DASHES;
	user: User;
	isLoading = false;
	isOffline = false;
	hasPrototypeDetailsError = false;
	prototypeRefernce: string;
	order?: Partial<FastLaneItems>;
	currentStatus: PrototypeStatus | undefined;
	proposalstatusmessage?: string;
	soldToList?: string[];
	bocs?: Map<string, BocClient>;
	userBocData?: SoldToRelationshipGroup[];
	sub: Subscription | undefined;
	soldToCodeValue?: string;
	statusMap = new Map<string, string>([
		['draft', ''],
		['requested', 'prototype.card.status.requested'],
		['proposal', 'prototype.status.proposal'],
		['decision', 'prototype.status.decision'],
		['confirmed', 'prototype.status.confirmed'],
		['closed', 'prototype.status.closed']
	]);

	constructor(
		private readonly route: ActivatedRoute,
		private readonly fastLaneService: FastLaneService,
		private readonly translationService: TranslationService,
		private readonly userService: UserService,
		readonly productCharacteristicsTranslationService: ProductCharacteristicsTranslationService,

	) {
		this.prototypeRefernce = <string> this.route.snapshot.params['supplierReference'];
		this.isOffline = !navigator.onLine;
		this.isLoading = true;
		this.user = this.userService.user;
	}

	ngOnInit(): void {
		this.sub = this.getInitialData().subscribe(
			{ next: (data: Responsedata | undefined) => {
				if (data) {
					const [userBocData, bocs] = data.bocValueList;
					this.userBocData = userBocData;
					this.isLoading = false;
					const bocsMap = this.bocs = new Map(bocs.map(v => [v.businessId, v]));
					this.soldToList = this.userBocData.map(v => v.soldTo);
					const soldToBocList: BocClient[] = this.soldToList.map(v => safe(bocsMap.get(v)));
					this.fastLaneService.filteredSoldtoShiptoList = soldToBocList;
					this.order = data.prototypeCardData;
					this.currentStatus = <PrototypeStatus> data.prototypeCardData.prototypeStatus;
					this.getSoldToCountryAndName(soldToBocList);
				}
			},
			error: (err: unknown) => {
				console.error('error retrieving InitialData', err);
				this.hasPrototypeDetailsError = true;
				this.isLoading = false;
			} }
		);
	}

	getStatusList() {
		if (this.order?.materialNotAvailable) {
			this.statusMap = new Map<string, string>([
				['draft', ''],
				['requested', 'prototype.card.status.requested'],
				['notavailable', 'prototype.card.status.notavailable']
			]);
		} else if (this.currentStatus === PrototypeStatus.ACCEPTED) {
			this.statusMap.set('decision', 'prototype.status.accepted');
		} else if (this.currentStatus === PrototypeStatus.REFUSED) {
			this.statusMap.set('decision', 'prototype.status.refused');
		}
		return Array.from(this.statusMap.values());
	}

	getCurrentIndex() {
		if (this.order?.poNumber && this.currentStatus === PrototypeStatus.ACCEPTED) {
			return CONFIRMED_STATUS_INDEX;
		} else if (this.currentStatus === PrototypeStatus.ACCEPTED || this.currentStatus === PrototypeStatus.REFUSED) {
			return DECISION_STATUS_INDEX;
		} else if (this.order?.materialNotAvailable) {
			return MATERIAL_AVAILABILTY_STATUS_INDEX;
		}
		return this.currentStatus ? Array.from(this.statusMap.keys()).indexOf(this.currentStatus) : 0;
	}

	ngOnDestroy(): void {
		if (this.sub !== undefined) {
			this.sub.unsubscribe();
		}
	}

	print() {
		print();
	}

	getRequestDimention() {
		return formatTextDimension(
			this.order?.thickness ? Number(Number(this.order.thickness).toFixed(2)) : '',
			this.order?.width ? Number(Number(this.order.width).toFixed(0)) : '',
			this.order?.length ? Number(Number(this.order.length).toFixed(0)) : '',
		);
	}

	compareDimentions(proposedDimention: string | undefined, requestedDimention: string) {
		if (proposedDimention !== undefined) {
			const proposedDim = this.extractNumbers(proposedDimention);
			const requestedDim = this.extractNumbers(requestedDimention);
			return proposedDim.length === requestedDim.length
				&& proposedDim.every((value, index) => value === requestedDim[index]);
		}
		return undefined;
	}

	extractNumbers(str: string): number[] {
		const matches = str.match(/\d+/g)?.map(Number);
		return matches ? matches.map(Number) : [];
	}

	replaceNullOrUndefinedWithDashes<T>(value: T): T | string {
		return value === null || value === undefined ? DASHES : value;
	}

	getCurrentStatuslabel() {
		const currentstatus = this.currentStatus;
		if (this.order?.noAvailabilityComment) {
			this.proposalstatusmessage = this.order.noAvailabilityComment;
		}
		if (this.order?.customerRefusal) {
			this.proposalstatusmessage = this.order.customerRefusal;
		}
		switch (currentstatus) {
		case PrototypeStatus.NOTAVAILABLE:
			return this.order?.materialNotAvailable ? this.translationService
				.getTR('prototype.card.status.materialnotfound')
				: '';
		case PrototypeStatus.REFUSED:
			return this.translationService.getTR('prototype.card.status.refusedbycustomer');
		case PrototypeStatus.ACCEPTED:
		case PrototypeStatus.CLOSED:
			return this.translationService.getTR('prototype.card.status.accepted');
		default:
			return '';
		}
	}

	getCurrencyValues() {
		let currencycodeValue;
		for (const currency of currencyList) {
			if (currency.code === this.order?.currency) {
				currencycodeValue = `${ this.order.currency } - ${ currency.description }`;
			}
		}
		return currencycodeValue;
	}

	getSoldToCountryAndName(soldToBocList: BocClient[]) {
		for (const soldTo of soldToBocList) {
			if (soldTo.businessId === this.order?.soldTo) {
				this.soldToCodeValue = `${ soldTo.name }/${ soldTo.city }`;
			}
		}
	}

	getRddWeek() {
		if (this.order?.requestedDeliveryDate) {
			return moment(this.order.requestedDeliveryDate).format(WEEKYEAR_FORMAT_PATTERN);
		}
		return DASHES;
	}

	isInProduction() {
		return this.order?.poNumber && (this.order.prototypeStatus === PrototypeStatus.CLOSED
			|| this.order.prototypeStatus === PrototypeStatus.ACCEPTED);
	}

	isInProposal() {
		return this.order?.prototypeStatus === PrototypeStatus.PROPOSAL
			|| this.order?.prototypeStatus === PrototypeStatus.ACCEPTED
			|| this.order?.prototypeStatus === PrototypeStatus.REFUSED
			|| this.order?.prototypeStatus === PrototypeStatus.CLOSED
			|| this.order?.prototypeStatus === PrototypeStatus.NOTAVAILABLE;
	}

	getObservableForCard() {
		return this.fastLaneService.getFastLaneItemsById(this.prototypeRefernce, 'INLINE');
	}

	private getInitialData(): Observable<Responsedata | undefined> {
		return this.getBocData().pipe(
			switchMap(bocValueList =>
				this.getObservableForCard().pipe(
					switchMap(
						prototypeCardData => of({ bocValueList, prototypeCardData })
							.pipe(catchError(
								(err: unknown) => {
									console.error('error retrieving card data', err);
									this.isLoading = false;
									return of(undefined);
								}
							))
					),

				).pipe(catchError(
					(err: unknown) => {
						const error = <HttpErrorResponse> err;
						if (!(err instanceof HttpErrorResponse)) {
							this.isLoading = false;
						}
						if (error.status === 400 || error.status === 500) {
							this.isLoading = false;
							this.hasPrototypeDetailsError = true;
						}
						console.error('error retrieving bocListValues', err);
						return of(undefined);
					}
				))),
		);
	}

	private getBocData() {
		return this.fastLaneService.getBocs().pipe(switchMap(userBocData => {
			if (userBocData.length === 0) {
				return of(<const>[userBocData, <BocClient[]>[]]);
			}
			const businessIds = Array.from(new Set(userBocData.flatMap(v => [v.soldTo, ...v.shipTos, ...v.endUsers])));
			return forkJoin([
				of(userBocData),
				this.userService.getBocs(
					['businessId', 'name', 'city', 'address', 'country', 'postalCode'],
					businessIds.map(x => `businessId:${ x }`).join(' OR ')
				).pipe(map(v => v.results))
			]);
		}));
	}
}
